import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Coins, CheckCircle2, Loader2 } from "lucide-react";
import { CAPITAL_PURPOSES } from "../../lib/quizFunderPreview";

/** One-time dashboard prompt for post-revenue founders who completed the
 *  quiz before the capital-purpose question existed. */
export const CapitalPurposePrompt = ({ comp, onSaved }) => {
  const [purposes, setPurposes] = useState([]);
  const [primary, setPrimary] = useState("");
  const [otherText, setOtherText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (!comp || hidden) return null;
  if ((comp.kind || "business") !== "business") return null;
  if ((comp.capital_purposes || []).length > 0) return null;
  const postRevenue = comp.get_started_quiz?.stage === "post_revenue"
    || ["early_revenue", "growth"].includes(comp.revenue_stage)
    || (comp.revenue_annual || 0) > 0;
  if (!postRevenue) return null;

  const toggle = (id) => {
    const next = purposes.includes(id) ? purposes.filter((x) => x !== id) : [...purposes, id];
    setPurposes(next);
    setPrimary(next.includes(primary) ? primary : (next[0] || ""));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/companies/me/capital-purposes", { purposes, primary, other: otherText.trim() });
      toast.success("Saved — funders will see what the capital is for");
      setHidden(true);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save — try again");
    } finally { setBusy(false); }
  };

  const valid = purposes.length > 0 && (!purposes.includes("other") || otherText.trim().length > 0);

  return (
    <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5" data-testid="capital-purpose-prompt">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-200/70">
          <Coins className="h-4.5 w-4.5 text-amber-800" />
        </div>
        <div>
          <p className="text-sm font-bold text-neutral-900">One quick question — what is the capital for?</p>
          <p className="text-xs text-neutral-600">Select all that apply and mark your main need. This sharpens which funders we match you with.</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {CAPITAL_PURPOSES.map((p) => {
          const active = purposes.includes(p.id);
          return (
            <button key={p.id} type="button" onClick={() => toggle(p.id)} title={p.hint}
                    data-testid={`purpose-chip-${p.id}`}
                    className={"rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors " +
                      (active ? "border-amber-500 bg-amber-500 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-amber-400")}>
              {p.label}
            </button>
          );
        })}
      </div>
      {purposes.includes("other") && (
        <textarea
          value={otherText}
          onChange={(e) => setOtherText(e.target.value.slice(0, 500))}
          rows={2}
          data-testid="capital-purpose-other-text"
          placeholder="Tell us more — e.g. buying out a partner, meeting a regulator's capital requirement, funding R&D…"
          className="mt-3 w-full rounded-lg border border-amber-300 bg-white p-2.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-amber-500 focus:outline-none"
        />
      )}
      {purposes.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">Main need:</span>
          {purposes.map((id) => (
            <button key={id} type="button" onClick={() => setPrimary(id)} data-testid={`purpose-primary-${id}`}
                    className={"inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold " +
                      (primary === id ? "border-emerald-600 bg-emerald-600 text-white" : "border-neutral-300 bg-white text-neutral-600")}>
              {primary === id && <CheckCircle2 className="h-3 w-3" />}
              {CAPITAL_PURPOSES.find((p) => p.id === id)?.label}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button onClick={save} disabled={busy || !valid} data-testid="capital-purpose-save"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
        </button>
        <button onClick={() => setHidden(true)} data-testid="capital-purpose-later"
                className="text-xs font-semibold text-neutral-500 hover:text-neutral-700">Remind me later</button>
      </div>
    </div>
  );
};
